import {Mapper} from '@s-ui/domain'

export class SeoDealersListMetaInformationMapper extends Mapper {
  constructor({config, seoDataValueObjectFactory, seoDataMapper, seoLiteralMapper}) {
    super()

    this._config = config
    this._seoConfig = this._config.get('seo')
    this._seoDataValueObjectFactory = seoDataValueObjectFactory
    this._seoDataMapper = seoDataMapper
    this._seoLiteralMapper = seoLiteralMapper
  }

  _parseCanonical = ({path}) => {
    const protocol = this._config.get('URL_PROTOCOL')
    const host = this._config.get('HOST_DOMAIN')

    return `${protocol}${host}${path}`
  }

  _buildRobots = ({nResults, metaRobots}) => {
    const {NO_CRAWLABLE_NOR_INDEXABLE_META_ROBOTS} = this._seoConfig

    return nResults ? metaRobots : NO_CRAWLABLE_NOR_INDEXABLE_META_ROBOTS
  }

  _getMetaConfig = ({city, dealersListConfig, province, make}) => {
    const hasFiltersApplied = Boolean(city || province || make)

    const dealersListPageConfig = hasFiltersApplied ? dealersListConfig.filtered : dealersListConfig.default
    return dealersListPageConfig
  }

  _getToponymLiteral = ({city, culture, province}) => {
    const hasCity = Boolean(city)
    const hasProvince = Boolean(province)

    let toponym
    if (hasCity) toponym = city
    if (!hasCity && hasProvince) toponym = province

    if (!toponym) return

    const {locatedInLiteral} = this._seoConfig

    return locatedInLiteral[culture]?.replace('%{location}', toponym)
  }

  _getMakeLiteral = ({make, culture}) => {
    if (!make) return undefined

    const {dealerWithMakeLiteral} = this._seoConfig

    return dealerWithMakeLiteral[culture]?.replace('%{make}', make)
  }

  _getPlaceholderValues = ({culture, searchParams}) => {
    const {city, province, make} = searchParams

    const placeholderValues = {
      make,
      dealerMake: this._getMakeLiteral({make, culture}),
      toponym: this._getToponymLiteral({city, culture, province})
    }

    return placeholderValues
  }

  async map({culture, params = {}}) {
    const {city, province, make, nResults, path} = params

    const {
      dealersList: dealersListConfig,
      CRAWLABLE_AND_INDEXABLE_META_ROBOTS,
      NO_CRAWLABLE_NOR_INDEXABLE_META_ROBOTS
    } = this._config.get('seo')

    const dealersListPageConfig = this._getMetaConfig({city, dealersListConfig, province, make})

    const {H1, TITLE, META_DESCRIPTION, META_ROBOTS} = dealersListPageConfig[culture]

    const placeholderValues = this._getPlaceholderValues({culture, searchParams: params})

    const parsedH1 = this._seoLiteralMapper.setText({text: H1}).setPlaceholderValues({placeholderValues}).map()

    const parsedTitle = this._seoLiteralMapper.setText({text: TITLE}).setPlaceholderValues({placeholderValues}).map()

    const parsedDescription = this._seoLiteralMapper
      .setText({text: META_DESCRIPTION})
      .setPlaceholderValues({placeholderValues})
      .map()

    const parsedMetaRobots = this._seoLiteralMapper
      .setText({text: META_ROBOTS})
      .setPlaceholderValues({
        placeholderValues: {
          metaRobots:
            nResults || !province ? CRAWLABLE_AND_INDEXABLE_META_ROBOTS : NO_CRAWLABLE_NOR_INDEXABLE_META_ROBOTS
        }
      })
      .map()

    const parsedCanonical = this._parseCanonical({
      path
    })

    const {links, metas, scripts, title} = this._seoDataMapper
      .title(parsedTitle)
      .description(parsedDescription)
      .robots(parsedMetaRobots)
      .canonical(parsedCanonical)
      .setOpenGraphData({
        title: parsedTitle,
        description: parsedDescription,
        url: parsedCanonical
      })
      .setTwitterCardsData()
      .map()

    return this._seoDataValueObjectFactory({
      h1: parsedH1,
      head: {
        link: links,
        meta: metas,
        scripts,
        title
      }
    })
  }
}
